/**
 * Diagnostic Guidance Hooks
 * 
 * Provides data fetching and mutations for the Diagnostic Guidance system.
 * All hooks are designed to work with empty data states gracefully.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route diagnostic-guidance `completed_at` write through clock.
import { clock } from '@/utils/clock';
import type {
  DiagnosticComplaint,
  DiagnosticFlow,
  DiagnosticStep,
  DiagnosticStepTraining,
  TicketDiagnosticSession,
  TicketDiagnosticStepProgress,
  DiagnosticFlowWithComplaint,
  DiagnosticStepWithTraining,
  DiagnosticSessionWithContext,
  CreateComplaintInput,
  CreateFlowInput,
  CreateStepInput,
  UpdateStepProgressInput,
  StartSessionInput,
  EndSessionInput,
  DiagnosticEventType,
} from '@/types/diagnosticGuidance';

// =====================================================
// QUERY HOOKS
// =====================================================

/**
 * Fetch all active diagnostic complaints
 */
export function useDiagnosticComplaints() {
  return useQuery({
    queryKey: ['diagnostic-complaints'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('diagnostic_complaints')
        .select('*')
        .eq('is_active', true)
        .order('label');
      
      if (error) throw error;
      return (data || []) as DiagnosticComplaint[];
    },
  });
}

/**
 * Fetch the canonical flow for a complaint
 */
export function useDiagnosticFlow(complaintId: string | undefined) {
  return useQuery({
    queryKey: ['diagnostic-flow', complaintId],
    queryFn: async () => {
      if (!complaintId) return null;
      
      const { data, error } = await supabase
        .from('diagnostic_flows')
        .select(`
          *,
          complaint:diagnostic_complaints(*)
        `)
        .eq('complaint_id', complaintId)
        .eq('is_canonical', true)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data as DiagnosticFlowWithComplaint | null;
    },
    enabled: !!complaintId,
  });
}

/**
 * Fetch all flows (for admin)
 */
export function useDiagnosticFlows() {
  return useQuery({
    queryKey: ['diagnostic-flows'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('diagnostic_flows')
        .select(`
          *,
          complaint:diagnostic_complaints(*)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as DiagnosticFlowWithComplaint[];
    },
  });
}

/**
 * Fetch steps for a flow
 */
export function useDiagnosticSteps(flowId: string | undefined) {
  return useQuery({
    queryKey: ['diagnostic-steps', flowId],
    queryFn: async () => {
      if (!flowId) return [];
      
      const { data, error } = await supabase
        .from('diagnostic_steps')
        .select('*')
        .eq('flow_id', flowId)
        .order('step_order');
      
      if (error) throw error;
      return (data || []) as DiagnosticStep[];
    },
    enabled: !!flowId,
  });
}

/**
 * Fetch training artifacts for a specific step
 */
export function useStepTraining(stepId: string | undefined) {
  return useQuery({
    queryKey: ['diagnostic-step-training', stepId],
    queryFn: async () => {
      if (!stepId) return [];
      
      const { data, error } = await supabase
        .from('diagnostic_step_training_map')
        .select(`
          *,
          artifact:training_artifacts(
            id,
            title,
            provider_name,
            artifact_type,
            access_url,
            access_type,
            estimated_time_minutes
          )
        `)
        .eq('step_id', stepId)
        .order('relevance_weight', { ascending: false });
      
      if (error) throw error;
      return (data || []) as DiagnosticStepWithTraining['training'];
    },
    enabled: !!stepId,
  });
}

/**
 * Fetch existing diagnostic session for a ticket
 */
export function useTicketDiagnosticSession(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['ticket-diagnostic-session', ticketId],
    queryFn: async () => {
      if (!ticketId) return null;
      
      const { data, error } = await supabase
        .from('ticket_diagnostic_sessions')
        .select(`
          *,
          flow:diagnostic_flows(
            *,
            complaint:diagnostic_complaints(*)
          )
        `)
        .eq('ticket_id', ticketId)
        .is('completed_at', null)
        .maybeSingle();
      
      if (error) throw error;
      
      if (!data) return null;
      
      // Fetch steps and progress for the session
      const [stepsResult, progressResult] = await Promise.all([
        supabase
          .from('diagnostic_steps')
          .select('*')
          .eq('flow_id', data.flow_id)
          .order('step_order'),
        supabase
          .from('ticket_diagnostic_steps')
          .select('*')
          .eq('session_id', data.id),
      ]);
      
      if (stepsResult.error) throw stepsResult.error;
      if (progressResult.error) throw progressResult.error;
      
      return {
        ...data,
        steps: stepsResult.data || [],
        progress: progressResult.data || [],
      } as DiagnosticSessionWithContext;
    },
    enabled: !!ticketId,
  });
}

/**
 * Fetch step progress for a session
 */
export function useSessionStepProgress(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['session-step-progress', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      
      const { data, error } = await supabase
        .from('ticket_diagnostic_steps')
        .select('*')
        .eq('session_id', sessionId);
      
      if (error) throw error;
      return (data || []) as TicketDiagnosticStepProgress[];
    },
    enabled: !!sessionId,
  });
}

// =====================================================
// MUTATION HOOKS
// =====================================================

/**
 * Start a new diagnostic session for a ticket
 */
export function useStartDiagnosticSession() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: StartSessionInput) => {
      // Create the session
      const { data: session, error: sessionError } = await supabase
        .from('ticket_diagnostic_sessions')
        .insert({
          ticket_id: input.ticket_id,
          flow_id: input.flow_id,
          technician_id: input.technician_id,
          technician_profile_id: input.technician_profile_id,
        })
        .select()
        .single();
      
      if (sessionError) throw sessionError;
      
      // Fetch steps for this flow to pre-create progress entries
      const { data: steps, error: stepsError } = await supabase
        .from('diagnostic_steps')
        .select('id')
        .eq('flow_id', input.flow_id)
        .order('step_order');
      
      if (stepsError) throw stepsError;
      
      // Pre-create progress entries for ALL steps to avoid race conditions
      if (steps && steps.length > 0) {
        const progressEntries = steps.map((step, index) => ({
          session_id: session.id,
          step_id: step.id,
          status: index === 0 ? 'in_progress' : 'pending',
        }));
        
        const { error: progressError } = await supabase
          .from('ticket_diagnostic_steps')
          .insert(progressEntries);
        
        if (progressError) throw progressError;
      }
      
      // Log behavior event
      await supabase.from('diagnostic_behavior_events').insert({
        session_id: session.id,
        technician_id: input.technician_id,
        technician_profile_id: input.technician_profile_id,
        event_type: 'session_started',
        metadata: { ticket_id: input.ticket_id, flow_id: input.flow_id },
      });
      
      return session as TicketDiagnosticSession;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-diagnostic-session', variables.ticket_id] });
    },
  });
}

/**
 * Update step progress
 */
export function useUpdateStepProgress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: UpdateStepProgressInput) => {
      const { data, error } = await supabase
        .from('ticket_diagnostic_steps')
        .update({
          status: input.status,
          technician_notes: input.technician_notes,
          result_flag: input.result_flag,
          completed_at: input.completed_at,
        })
        .eq('id', input.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as TicketDiagnosticStepProgress;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['session-step-progress', data.session_id] });
      // Also invalidate the ticket diagnostic session to trigger UI update and advance to next step
      queryClient.invalidateQueries({ queryKey: ['ticket-diagnostic-session'] });
    },
  });
}

/**
 * Create step progress entry
 */
export function useCreateStepProgress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: { session_id: string; step_id: string; status?: string }) => {
      const { data, error } = await supabase
        .from('ticket_diagnostic_steps')
        .insert({
          session_id: input.session_id,
          step_id: input.step_id,
          status: input.status || 'in_progress',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as TicketDiagnosticStepProgress;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['session-step-progress', data.session_id] });
    },
  });
}

/**
 * End a diagnostic session
 */
export function useEndDiagnosticSession() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: EndSessionInput) => {
      const { data, error } = await supabase
        .from('ticket_diagnostic_sessions')
        .update({
          completed_at: clock.now().toISOString(),
          outcome: input.outcome,
        })
        .eq('id', input.id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log behavior event
      await supabase.from('diagnostic_behavior_events').insert({
        session_id: input.id,
        event_type: input.outcome === 'completed' ? 'session_completed' : 'session_abandoned',
        metadata: { outcome: input.outcome },
      });
      
      return data as TicketDiagnosticSession;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-diagnostic-session', data.ticket_id] });
    },
  });
}

/**
 * Log a behavior event
 */
export function useLogDiagnosticEvent() {
  return useMutation({
    mutationFn: async (input: {
      session_id?: string;
      step_id?: string;
      technician_id?: string;
      technician_profile_id?: string;
      event_type: DiagnosticEventType;
      metadata?: Record<string, unknown>;
    }) => {
      const { error } = await supabase.from('diagnostic_behavior_events').insert([{
        session_id: input.session_id,
        step_id: input.step_id,
        technician_id: input.technician_id,
        technician_profile_id: input.technician_profile_id,
        event_type: input.event_type,
        metadata: (input.metadata || {}) as unknown as Record<string, never>,
      }]);
      
      if (error) throw error;
    },
  });
}

// =====================================================
// ADMIN MUTATION HOOKS
// =====================================================

/**
 * Create a new complaint
 */
export function useCreateComplaint() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateComplaintInput) => {
      const { data, error } = await supabase
        .from('diagnostic_complaints')
        .insert(input)
        .select()
        .single();
      
      if (error) throw error;
      return data as DiagnosticComplaint;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-complaints'] });
    },
  });
}

/**
 * Update a complaint
 */
export function useUpdateComplaint() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: Partial<DiagnosticComplaint> & { id: string }) => {
      const { data, error } = await supabase
        .from('diagnostic_complaints')
        .update({
          key: input.key,
          label: input.label,
          description: input.description,
          is_active: input.is_active,
        })
        .eq('id', input.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as DiagnosticComplaint;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-complaints'] });
    },
  });
}

/**
 * Create a new flow
 */
export function useCreateFlow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateFlowInput) => {
      const { data, error } = await supabase
        .from('diagnostic_flows')
        .insert(input)
        .select()
        .single();
      
      if (error) throw error;
      return data as DiagnosticFlow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-flows'] });
    },
  });
}

/**
 * Create a new step
 */
export function useCreateStep() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateStepInput) => {
      const { data, error } = await supabase
        .from('diagnostic_steps')
        .insert(input)
        .select()
        .single();
      
      if (error) throw error;
      return data as DiagnosticStep;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-steps', variables.flow_id] });
    },
  });
}

/**
 * Update a step
 */
export function useUpdateStep() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: Partial<DiagnosticStep> & { id: string; flow_id: string }) => {
      const { data, error } = await supabase
        .from('diagnostic_steps')
        .update({
          step_order: input.step_order,
          title: input.title,
          description: input.description,
          is_optional: input.is_optional,
          skill_depth: input.skill_depth,
        })
        .eq('id', input.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as DiagnosticStep;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-steps', variables.flow_id] });
    },
  });
}

/**
 * Delete a step
 */
export function useDeleteStep() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: { id: string; flow_id: string }) => {
      const { error } = await supabase
        .from('diagnostic_steps')
        .delete()
        .eq('id', input.id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-steps', variables.flow_id] });
    },
  });
}

/**
 * Add training to a step
 */
export function useAddStepTraining() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: { step_id: string; artifact_id: string; training_role: string; relevance_weight?: number }) => {
      const { data, error } = await supabase
        .from('diagnostic_step_training_map')
        .insert({
          step_id: input.step_id,
          artifact_id: input.artifact_id,
          training_role: input.training_role,
          relevance_weight: input.relevance_weight || 1.0,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as DiagnosticStepTraining;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-step-training', variables.step_id] });
    },
  });
}

/**
 * Remove training from a step
 */
export function useRemoveStepTraining() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: { id: string; step_id: string }) => {
      const { error } = await supabase
        .from('diagnostic_step_training_map')
        .delete()
        .eq('id', input.id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic-step-training', variables.step_id] });
    },
  });
}

// =====================================================
// MATCHER-BASED SESSION CREATION
// =====================================================

import type { VehicleContext } from '@/utils/diagnosticComplaintMatcher';
import type { Json } from '@/integrations/supabase/types';

interface StartSessionWithMatcherInput {
  ticket_id: string;
  complaint_key: string;
  technician_id?: string;
  technician_profile_id?: string;
  vehicle_context: VehicleContext;
}

interface SessionResult {
  session: TicketDiagnosticSession;
  wasResumed: boolean;
}

/**
 * Start a diagnostic session from a matched complaint key.
 * Resumes existing active session if one exists for the same ticket+flow.
 */
export function useStartDiagnosticSessionWithMatcher() {
  const queryClient = useQueryClient();
  
  return useMutation<SessionResult, Error, StartSessionWithMatcherInput>({
    mutationFn: async (input) => {
      // 1. Look up complaint by key
      const { data: complaint, error: complaintError } = await supabase
        .from('diagnostic_complaints')
        .select('id')
        .eq('key', input.complaint_key)
        .eq('is_active', true)
        .maybeSingle();

      if (complaintError) throw complaintError;
      if (!complaint) throw new Error(`Complaint not found: ${input.complaint_key}`);

      // 2. Look up canonical flow for complaint
      const { data: flow, error: flowError } = await supabase
        .from('diagnostic_flows')
        .select('id')
        .eq('complaint_id', complaint.id)
        .eq('is_canonical', true)
        .eq('is_active', true)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (flowError) throw flowError;
      if (!flow) throw new Error(`No active canonical flow for complaint: ${input.complaint_key}`);

      // 3. Check for existing ACTIVE session (resume logic)
      const { data: existingSession, error: existingError } = await supabase
        .from('ticket_diagnostic_sessions')
        .select('*')
        .eq('ticket_id', input.ticket_id)
        .eq('flow_id', flow.id)
        .is('completed_at', null)
        .maybeSingle();

      if (existingError) throw existingError;

      // Resume existing session if found
      if (existingSession) {
        console.log('[DiagMatcher] Resuming existing session:', existingSession.id);
        return { 
          session: existingSession as TicketDiagnosticSession, 
          wasResumed: true 
        };
      }

      // 4. Create NEW session WITH vehicle_context and current_step_id
      // First get all steps to find the first one and seed progress
      const { data: allSteps, error: allStepsError } = await supabase
        .from('diagnostic_steps')
        .select('id')
        .eq('flow_id', flow.id)
        .order('step_order');

      if (allStepsError) throw allStepsError;
      const firstStepId = allSteps && allSteps.length > 0 ? allSteps[0].id : null;

      const { data: session, error: sessionError } = await supabase
        .from('ticket_diagnostic_sessions')
        .insert({
          ticket_id: input.ticket_id,
          flow_id: flow.id,
          technician_id: input.technician_id,
          technician_profile_id: input.technician_profile_id,
          vehicle_context: input.vehicle_context as unknown as Json,
          current_step_id: firstStepId,  // Set initial step pointer
        })
        .select()
        .single();

      if (sessionError) throw sessionError;

      // 5. Seed step progress (matches existing pattern)
      if (allSteps && allSteps.length > 0) {
        const progressRecords = allSteps.map((step, index) => ({
          session_id: session.id,
          step_id: step.id,
          status: index === 0 ? 'in_progress' : 'pending',
        }));

        const { error: progressError } = await supabase
          .from('ticket_diagnostic_steps')
          .insert(progressRecords);

        if (progressError) throw progressError;
      }

      // Log behavior event
      await supabase.from('diagnostic_behavior_events').insert({
        session_id: session.id,
        technician_id: input.technician_id,
        technician_profile_id: input.technician_profile_id,
        event_type: 'session_started',
        metadata: { 
          ticket_id: input.ticket_id, 
          flow_id: flow.id,
          complaint_key: input.complaint_key,
          vehicle_context: input.vehicle_context
        } as unknown as Record<string, never>,
      });

      return { 
        session: session as TicketDiagnosticSession, 
        wasResumed: false 
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-diagnostic-session', variables.ticket_id] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-matcher', variables.ticket_id] });
    },
  });
}
